import * as Dialog from '@radix-ui/react-dialog';
import { Info, AlertTriangle, AlertCircle, CheckCircle, X } from 'lucide-react';
import { useMemo, useState } from 'react';

import { useLoginAnnouncements, useDismissNotification } from '@/hooks/useNotifications';
import { NotificationActionButton } from './NotificationActionButton';

const typeIcons = {
  info: Info,
  warning: AlertTriangle,
  error: AlertCircle,
  success: CheckCircle,
} as const;

const typeColors = {
  info: 'text-sky-600 dark:text-sky-400',
  warning: 'text-amber-600 dark:text-amber-400',
  error: 'text-red-600 dark:text-red-400',
  success: 'text-green-600 dark:text-green-400',
} as const;

export function LoginAnnouncementModal() {
  const { data, isLoading } = useLoginAnnouncements();
  const dismiss = useDismissNotification();
  const [markedIds, setMarkedIds] = useState<Set<string>>(new Set());
  const [closed, setClosed] = useState(false);

  // Filter out announcements we've already marked read in this session
  const remaining = useMemo(() => {
    const all = data?.announcements ?? [];
    return all.filter((a) => !markedIds.has(a.id));
  }, [data?.announcements, markedIds]);

  const [current] = remaining;
  if (isLoading || closed || !current) {
    return null;
  }

  const isLast = remaining.length === 1;
  const Icon = typeIcons[current.type];
  const colorClass = typeColors[current.type];
  const visibleActions = current.actions.filter((a) => a.type !== 'dismiss');

  const handleDismiss = () => {
    dismiss.mutate(current.id, {
      onSuccess: () => {
        setMarkedIds((prev) => new Set(prev).add(current.id));
        if (isLast) {
          setClosed(true);
        }
      },
    });
  };

  const handleRemindLater = () => {
    setClosed(true);
  };

  return (
    <Dialog.Root open onOpenChange={() => handleRemindLater()}>
      <Dialog.Portal>
        <Dialog.Overlay
          className="fixed inset-0 bg-black/50 z-50
                     data-[state=open]:animate-in data-[state=closed]:animate-out
                     data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        />
        <Dialog.Content
          className="fixed left-[50%] top-[50%] z-50 w-full max-w-lg
                     translate-x-[-50%] translate-y-[-50%]
                     bg-white dark:bg-zinc-800 shadow-lg rounded-lg
                     data-[state=open]:animate-in data-[state=closed]:animate-out
                     data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0
                     data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          aria-describedby="announcement-body"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-zinc-200 dark:border-zinc-700">
            <div className="flex items-center gap-3">
              <Icon className={`h-6 w-6 ${colorClass}`} />
              <Dialog.Title className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
                {current.title}
              </Dialog.Title>
            </div>
            <Dialog.Close
              className="rounded-xs opacity-70 hover:opacity-100
                         focus:outline-hidden focus:ring-2 focus:ring-sky-400"
            >
              <X size={20} className="text-zinc-500 dark:text-zinc-400" />
              <span className="sr-only">Close</span>
            </Dialog.Close>
          </div>

          {/* Body */}
          <div id="announcement-body" className="p-6">
            <p className="text-sm text-zinc-700 dark:text-zinc-300 whitespace-pre-wrap">
              {current.message}
            </p>

            {visibleActions.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {visibleActions.map((action, idx) => (
                  <NotificationActionButton
                    key={idx}
                    action={action}
                    notificationId={current.id}
                    onClose={handleDismiss}
                  />
                ))}
              </div>
            )}

            {remaining.length > 1 && (
              <p className="mt-4 text-xs text-zinc-400 dark:text-zinc-500">
                1 of {remaining.length} remaining
              </p>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 p-6 border-t border-zinc-200 dark:border-zinc-700">
            <button
              onClick={handleRemindLater}
              className="px-4 py-2 text-sm font-medium text-zinc-700 dark:text-zinc-300
                         bg-white dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-600
                         rounded-lg hover:bg-zinc-50 dark:hover:bg-zinc-700
                         focus:outline-hidden focus:ring-2 focus:ring-sky-500"
            >
              Remind me later
            </button>
            <button
              onClick={handleDismiss}
              disabled={dismiss.isPending}
              className="px-4 py-2 text-sm font-medium text-white
                         bg-sky-600 hover:bg-sky-700 rounded-lg
                         focus:outline-hidden focus:ring-2 focus:ring-sky-500
                         disabled:opacity-50"
            >
              {isLast ? 'Dismiss' : 'Next'}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
